import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconSuccess } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import { UNIFIED_INBOX_ENABLED } from '../../config/unifiedInboxAccess';
import { Button } from '../common/Button';
import type { CockpitMandantSummary } from '../../api/advisorCockpitApi';

/**
 * M-UX-7.2 (`plan-ux-7.2-mandanten.md` §2.2) — Zellen-Bausteine EINER
 * Mandanten-Zeile der Kanzlei-Übersicht (`MandantenPage`).
 *
 * **Architektur-Notiz (Coder-Entscheidung, in changelog-ux-w72.md dokumentiert):**
 * `common/DataTable` ist spalten-basiert — jede Spalte rendert über ihren
 * eigenen `accessor` ein `<td>`. Eine einzelne Komponente kann daher NICHT
 * mehrere sortierbare Ampel-Spalten (Kritisch/Prüfen/Geprüft, §2.3 + §7a
 * `initialSort` red-desc) überspannen. Deshalb ist die Ampel-Darstellung in
 * kleine, pro-Spalte einsetzbare Zell-Bausteine aufgeteilt
 * ({@link AmpelCountCell} / {@link CheckedCell}), und {@link MandantRow} selbst
 * rendert die drei Zeilen-Aktionen (Aktionen-Spalte, §2.3). Alle Bausteine
 * liegen bewusst in DIESER Datei, sodass die §7c-Ampel-Assertions
 * (Trio/Strich/Häkchen) und die §7c-Aktions-/disabled-Assertions gemeinsam
 * gegen `MandantRow.tsx` getestet werden können.
 *
 * @module components/advisor/MandantRow
 */

/** Metrik-Wahl einer Ampel-Zähler-Zelle. */
type AmpelKind = 'red' | 'yellow';

/**
 * Ruhiger „—"-Strich für Zellen ohne Prüfdaten (Nicht-Beta-Mandant, Summary-
 * Ausfall). Bewusst gedämpft (`text-muted`) und aria-beschriftet, damit die
 * Zelle informativ-leer statt „kaputt" wirkt (§2.2, D4). Rechtsbündig über die
 * `align:'right'`-Spalte.
 */
function NoData(): React.ReactElement {
  const { t } = useTranslation('common');
  return (
    <span className="text-muted" aria-label={t('mandanten.noSummaryAria')}>
      {t('mandanten.noSummary')}
    </span>
  );
}

/**
 * Ruhiger Skeleton-Balken für eine Ampel-Zelle, während der (asynchrone,
 * per-Beta) Left-Join der Prüf-Summary noch lädt (D3/Ziel 6). Die Mandats-
 * Zeilen (Name + Aktionen) stehen sofort — nur die Ampel-Ziffern „shimmern"
 * nach. `motion-reduce:animate-none` respektiert `prefers-reduced-motion`.
 * `aria-hidden`, da die zentrale Lade-Ansage auf Seitenebene sitzt.
 *
 * @param widthClass - Tailwind-Breitenklasse (schmaler für Zähler, breiter für
 *   „Geprüft"), damit der Platzhalter der späteren Ziffernbreite nahekommt.
 */
function CellSkeleton({ widthClass }: { widthClass: string }): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-3.5 ${widthClass} animate-pulse rounded bg-surface-inset align-middle motion-reduce:animate-none`}
    />
  );
}

export interface AmpelCountCellProps {
  kind: AmpelKind;
  /** `null` ⇒ keine Prüfdaten (Nicht-Beta-Mandant ODER Summary-Ausfall) → ruhiger Strich. */
  summary: CockpitMandantSummary | null;
  /** `true` während der Beta-Summary-Call läuft ⇒ Skeleton statt Strich (Ziel 6). */
  loading?: boolean;
}

/**
 * Rendert einen einzelnen Ampel-Zähler (Kritisch = rot / Prüfen = gelb) einer
 * Mandanten-Zeile als Ledger-Ziffer. Reihenfolge der Zustände: lädt ⇒ Skeleton;
 * `summary === null` ⇒ ruhiger „—"-Strich (kein Fehler-Ton, §2.2); sonst die
 * Ziffer. Zähler `> 0` werden farblich betont (Kritisch = `red-300` semibold,
 * schwerer als Prüfen = `amber-300` medium ⇒ Kritisch dominiert; beide Töne
 * ≥ AA auch auf der Hover-Fläche), `0` bleibt als ehrliche, gedämpfte Ledger-
 * Null stehen. Die Ziffern erben `tabular-nums`/Rechtsbündigkeit aus der
 * `DataTable`-Spalte (`numeric` + `align:'right'`) ⇒ saubere Zahlenkolonne.
 *
 * Farbe ist NICHT alleiniger Informationsträger (D5): Spalten-Kopf
 * (Kritisch/Prüfen) + Position + Ziffernwert kodieren die Bedeutung auch ohne
 * Farbe; die aria-Beschriftung nennt sie explizit.
 *
 * @param props - {@link AmpelCountCellProps}
 * @returns die Zell-Darstellung (Skeleton, Strich oder farbige Ziffer).
 */
export function AmpelCountCell({ kind, summary, loading = false }: AmpelCountCellProps): React.ReactElement {
  const { t } = useTranslation('common');

  if (loading) return <CellSkeleton widthClass="w-6" />;
  if (summary === null) return <NoData />;

  const count = kind === 'red' ? summary.redCount : summary.yellowCount;
  const toneClass =
    count === 0
      ? 'text-muted'
      : kind === 'red'
        ? 'text-[var(--tone-danger-fg)] font-semibold'
        : 'text-[var(--tone-warning-fg)] font-medium';
  const ariaLabel =
    kind === 'red'
      ? t('mandanten.countAria.red', { count })
      : t('mandanten.countAria.yellow', { count });

  return (
    <span className={toneClass} aria-label={ariaLabel}>
      {count}
    </span>
  );
}

export interface CheckedCellProps {
  /** `null` ⇒ keine Prüfdaten → ruhiger Strich. */
  summary: CockpitMandantSummary | null;
  /** `true` während der Beta-Summary-Call läuft ⇒ Skeleton statt Strich (Ziel 6). */
  loading?: boolean;
}

/**
 * Rendert die „Geprüft"-Zelle: Anzahl geprüfter Belege plus — falls nichts
 * offen ist (`flaggedCount === 0`) — ein grünes „alles geprüft"-Häkchen
 * (Muster `AdvisorCockpitPage`, `emerald-400` ≥ AA-Graphik). Reihenfolge:
 * lädt ⇒ Skeleton; `summary === null` ⇒ „—"; sonst Ziffer (+ ggf. Häkchen).
 *
 * @param props - {@link CheckedCellProps}
 * @returns die Zell-Darstellung.
 */
export function CheckedCell({ summary, loading = false }: CheckedCellProps): React.ReactElement {
  const { t } = useTranslation('common');

  if (loading) return <CellSkeleton widthClass="w-8" />;
  if (summary === null) return <NoData />;

  const allClear = summary.flaggedCount === 0;

  return (
    <span
      className="inline-flex items-center justify-end gap-1.5 text-secondary"
      aria-label={t('mandanten.countAria.checked', { count: summary.totalChecked })}
    >
      {summary.totalChecked}
      {allClear && (
        <IconSuccess size={14} className="text-[var(--tone-success-fg)]" aria-hidden={false} aria-label={t('mandanten.allClear')} />
      )}
    </span>
  );
}

export interface MandantRowProps {
  /** Bereits aufgelöste Anzeigename-Fallback-Kette (§1) — für die Aktions-`aria-label`. */
  displayName: string;
  /**
   * Ampel-Summary des Mandanten (`null` = keine Prüfdaten). Für die Aktionen
   * nicht benötigt; Bestandteil der fixierten §2.2-Signatur (Ampel-Rendering
   * übernehmen {@link AmpelCountCell}/{@link CheckedCell} in eigenen Spalten).
   */
  summary: CockpitMandantSummary | null;
  /** `null` NUR bei der verwaisten Defensive-Zeile (E4) ⇒ Aktionen deaktiviert. */
  tenantId: string | null;
}

/**
 * Rendert die drei Zeilen-Aktionen eines Mandanten (Aktionen-Spalte, §2.2/§2.3):
 * „Cockpit öffnen" (primär), „Belege", „DATEV". Jede Aktion wechselt zuerst den
 * Tenant (`switchTenant(tenantId)`, synchron) und navigiert dann zum Ziel — in
 * genau dieser Reihenfolge (E4). Bei `tenantId === null` sind alle Aktionen
 * `disabled` (Defensive, kein Schreib-/Mutations-Pfad).
 *
 * Self-contained analog `TenantSwitcher`/`AdvisorContextBar`: liest
 * `switchTenant` aus {@link useAuth}, navigiert via `useNavigate`.
 *
 * @param props - {@link MandantRowProps}
 * @returns die Aktions-Button-Gruppe der Zeile.
 */
/**
 * Gemeinsame Klassen aller drei Zeilen-Aktionen: sichtbarer Mint-Fokusring
 * (`ring-inset`, ohne Offset-Farbe ⇒ token-frei robust; Ziel 3) und
 * `whitespace-nowrap`, damit kein Aktions-Label in einer der 6 Sprachen
 * mitten im Wort umbricht (Ziel 5). Überbreite fängt die `overflow-x-auto`-
 * DataTable ab, statt die Zeilenhöhe zu sprengen.
 */
const ACTION_CLASSES =
  'whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset';

export const MandantRow: React.FC<MandantRowProps> = ({ displayName, tenantId }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { switchTenant } = useAuth();

  const disabled = tenantId === null;
  const receiptsTarget = UNIFIED_INBOX_ENABLED ? '/dashboard/eingaenge' : '/dashboard/posteingang';

  const openMandant = (event: React.MouseEvent, target: string) => {
    // Row-`onClick` (= Cockpit öffnen) darf durch die dedizierten Aktions-
    // Buttons NICHT zusätzlich ausgelöst werden.
    event.stopPropagation();
    if (tenantId === null) return;
    switchTenant(tenantId);
    navigate(target);
  };

  // Hierarchie ohne „Farbmatsch" (D2/Ziel 3): „Cockpit öffnen" ist die primäre
  // Aktion — `secondary` (umrandet, heller Text) hebt sie ruhig von den beiden
  // `ghost`-Nebenaktionen (Belege/DATEV) ab, OHNE einen lauten Mint-Button in
  // jeder Ledger-Zeile zu setzen. Die eigentliche Primär-Affordanz bleibt der
  // Klick auf die ganze Zeile (Cursor/Hover), diese Buttons sind die explizite,
  // tastaturbare Doublette.
  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        size="sm"
        variant="secondary"
        disabled={disabled}
        className={ACTION_CLASSES}
        aria-label={t('mandanten.actionAria.cockpit', { name: displayName })}
        onClick={(event) => openMandant(event, '/dashboard/cockpit')}
      >
        {t('mandanten.action.cockpit')}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled}
        className={ACTION_CLASSES}
        aria-label={t('mandanten.actionAria.receipts', { name: displayName })}
        onClick={(event) => openMandant(event, receiptsTarget)}
      >
        {t('mandanten.action.receipts')}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={disabled}
        className={ACTION_CLASSES}
        aria-label={t('mandanten.actionAria.datev', { name: displayName })}
        onClick={(event) => openMandant(event, '/dashboard/datev-sammelexport')}
      >
        {t('mandanten.action.datev')}
      </Button>
    </div>
  );
};

export default MandantRow;
